import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  FaUserFriends, 
  FaWallet, 
  FaChartLine, 
  FaUser,
  FaUsers,
  FaBox,
  FaImage,
  FaNewspaper,
  FaGavel,
  FaEnvelope,
  FaHome,
  FaBuilding,
  FaHandHoldingUsd,
  FaCrown,
  FaRocket,
  FaGem
} from "react-icons/fa";
import {
  FiHome,
  FiImage,
  FiFileText,
  FiPhone,
  FiUsers,
  FiMapPin,
  FiLink,
  FiActivity,
  FiTrendingUp,
  FiDollarSign,
  FiCheckCircle,
  FiClock,
  FiBarChart2,
  FiGrid,
  FiList,
  FiEye
} from "react-icons/fi";
import { sendApiRequest } from "../../utils.js";

const AdminOverview = () => {
  const [count, setCount] = useState({});
  const [overviewStats, setOverviewStats] = useState({
    directStats: { active: 0, inactive: 0 },
    teamStats: { active: 0, inactive: 0 },
    businessStats: { self: 0, team: 0 },
    walletStats: { mainWallet: 0, totalIncome: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const countsResponse = await sendApiRequest('api/admin/get_all_counts', {}, 'GET');
        setCount(countsResponse.data || {});

        const statsResponse = await sendApiRequest('api/overview/stats', {}, 'GET');
        if (statsResponse.status === 200) {
          setOverviewStats(statsResponse.data.data);
        }

        // Mock recent activities - replace with actual API call
        setRecentActivities([
          { id: 1, action: "New user registered", user: "John Doe", time: "2 minutes ago", type: "user" },
          { id: 2, action: "Package purchased", user: "Jane Smith", amount: "₹25,000", time: "15 minutes ago", type: "sale" },
          { id: 3, action: "Withdrawal request", user: "Mike Johnson", amount: "₹5,000", time: "1 hour ago", type: "withdrawal" },
          { id: 4, action: "KYC submitted", user: "Sarah Williams", time: "3 hours ago", type: "kyc" },
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'user': return <FiUsers className="w-4 h-4 text-blue-500" />;
      case 'sale': return <FiDollarSign className="w-4 h-4 text-emerald-500" />;
      case 'withdrawal': return <FiTrendingUp className="w-4 h-4 text-amber-500" />;
      case 'kyc': return <FiCheckCircle className="w-4 h-4 text-purple-500" />;
      default: return <FiActivity className="w-4 h-4 text-gray-500" />;
    }
  };

  const mainStatsCards = [
    {
      icon: <FaUserFriends className="w-6 h-6" />,
      label: "Direct Team",
      value: `${overviewStats.directStats.active}/${overviewStats.directStats.inactive}`,
      subLabel: "Active/Inactive",
      bg: "from-purple-500 to-purple-600",
      gradient: "bg-gradient-to-br from-purple-50 to-purple-100",
      textColor: "text-purple-700",
      borderColor: "border-purple-200"
    },
    {
      icon: <FaUsers className="w-6 h-6" />,
      label: "Total Team",
      value: `${overviewStats.teamStats.active}/${overviewStats.teamStats.inactive}`,
      subLabel: "Active/Inactive",
      bg: "from-orange-500 to-orange-600",
      gradient: "bg-gradient-to-br from-orange-50 to-orange-100",
      textColor: "text-orange-700",
      borderColor: "border-orange-200"
    },
    {
      icon: <FaChartLine className="w-6 h-6" />,
      label: "Business Volume",
      value: formatAmount(overviewStats.businessStats.self + overviewStats.businessStats.team),
      subLabel: "Total Business",
      bg: "from-red-500 to-red-600",
      gradient: "bg-gradient-to-br from-red-50 to-red-100",
      textColor: "text-red-700",
      borderColor: "border-red-200"
    },
    {
      icon: <FaWallet className="w-6 h-6" />,
      label: "Wallet Balance",
      value: formatAmount(overviewStats.walletStats.totalIncome),
      subLabel: "Available Balance",
      bg: "from-emerald-500 to-emerald-600",
      gradient: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      textColor: "text-emerald-700",
      borderColor: "border-emerald-200"
    }
  ];

  const statsCards = [
    {
      icon: <FiHome className="w-5 h-5" />,
      label: "Properties",
      value: count.plots || 0,
      subLabel: "Total Plots",
      bg: "from-blue-500 to-blue-600",
      gradient: "bg-gradient-to-br from-blue-50 to-blue-100",
      textColor: "text-blue-700",
      borderColor: "border-blue-200"
    },
    {
      icon: <FiFileText className="w-5 h-5" />,
      label: "Packages",
      value: count.plans || 0,
      subLabel: "Active Plans",
      bg: "from-indigo-500 to-indigo-600",
      gradient: "bg-gradient-to-br from-indigo-50 to-indigo-100",
      textColor: "text-indigo-700",
      borderColor: "border-indigo-200"
    },
    {
      icon: <FiImage className="w-5 h-5" />,
      label: "Gallery",
      value: count.galaryItems || 0,
      subLabel: "Images",
      bg: "from-yellow-500 to-yellow-600",
      gradient: "bg-gradient-to-br from-yellow-50 to-yellow-100",
      textColor: "text-yellow-700",
      borderColor: "border-yellow-200"
    },
    {
      icon: <FiLink className="w-5 h-5" />,
      label: "News",
      value: count.newsItems || 0,
      subLabel: "Articles",
      bg: "from-pink-500 to-pink-600",
      gradient: "bg-gradient-to-br from-pink-50 to-pink-100",
      textColor: "text-pink-700",
      borderColor: "border-pink-200"
    },
    {
      icon: <FiMapPin className="w-5 h-5" />,
      label: "Legal Docs",
      value: count.legalDocs || 0,
      subLabel: "Documents",
      bg: "from-cyan-500 to-cyan-600",
      gradient: "bg-gradient-to-br from-cyan-50 to-cyan-100",
      textColor: "text-cyan-700",
      borderColor: "border-cyan-200"
    },
    {
      icon: <FiPhone className="w-5 h-5" />,
      label: "Contact Inquiries",
      value: count.contactMessages || 0,
      subLabel: "Messages",
      bg: "from-teal-500 to-teal-600",
      gradient: "bg-gradient-to-br from-teal-50 to-teal-100",
      textColor: "text-teal-700",
      borderColor: "border-teal-200"
    },
    {
      icon: <FiUsers className="w-5 h-5" />,
      label: "Team Members",
      value: count.teamMembers || 0,
      subLabel: "Total Users",
      bg: "from-purple-500 to-purple-600",
      gradient: "bg-gradient-to-br from-purple-50 to-purple-100",
      textColor: "text-purple-700",
      borderColor: "border-purple-200"
    },
    {
      icon: <FaHandHoldingUsd className="w-5 h-5" />,
      label: "Withdrawals",
      value: count.withdrawals || 0,
      subLabel: "Pending Requests",
      bg: "from-amber-500 to-amber-600",
      gradient: "bg-gradient-to-br from-amber-50 to-amber-100",
      textColor: "text-amber-700",
      borderColor: "border-amber-200"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaGem className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">Welcome back, Administrator</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live System</span>
              </span>
            </div>
          </div>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {mainStatsCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4 }}
              className={`bg-white rounded-2xl p-5 shadow-lg border ${card.borderColor} overflow-hidden relative group`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${card.bg} flex items-center justify-center mb-3 shadow-lg group-hover:scale-110 transition`}>
                    {React.cloneElement(card.icon, { className: "w-6 h-6 text-white" })}
                  </div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{card.value}</p>
                  <p className="text-xs text-gray-400 mt-1">{card.subLabel}</p>
                </div>
                <div className={`w-20 h-20 rounded-full ${card.gradient} opacity-50 -mr-6 -mt-6`}></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Business Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <FiTrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Business Breakdown</h3>
                <p className="text-xs text-gray-500">Self vs Team Business</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Self Business</span>
                  <span className="font-semibold text-blue-600">{formatAmount(overviewStats.businessStats.self)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full"
                    style={{ width: `${(overviewStats.businessStats.self / (overviewStats.businessStats.self + overviewStats.businessStats.team)) * 100 || 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Team Business</span>
                  <span className="font-semibold text-purple-600">{formatAmount(overviewStats.businessStats.team)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                    style={{ width: `${(overviewStats.businessStats.team / (overviewStats.businessStats.self + overviewStats.businessStats.team)) * 100 || 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-800">Total Business</span>
                  <span className="font-bold text-xl text-indigo-600">
                    {formatAmount(overviewStats.businessStats.self + overviewStats.businessStats.team)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Wallet Breakdown */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <FaWallet className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Wallet Summary</h3>
                <p className="text-xs text-gray-500">Balance Overview</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Main Wallet</span>
                  <span className="font-semibold text-emerald-600">{formatAmount(overviewStats.walletStats.mainWallet)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full"
                    style={{ width: `${(overviewStats.walletStats.mainWallet / overviewStats.walletStats.totalIncome) * 100 || 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Total Income</span>
                  <span className="font-semibold text-purple-600">{formatAmount(overviewStats.walletStats.totalIncome)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                    style={{ width: `${(overviewStats.walletStats.totalIncome / overviewStats.walletStats.totalIncome) * 100 || 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {statsCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + (index * 0.05) }}
              whileHover={{ y: -2 }}
              className={`bg-white rounded-xl p-4 shadow-md border ${card.borderColor} hover:shadow-lg transition-all group`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${card.bg} flex items-center justify-center group-hover:scale-110 transition`}>
                  {React.cloneElement(card.icon, { className: "w-5 h-5 text-white" })}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">{card.label}</p>
                  <p className="text-xl font-bold text-gray-800">{card.value}</p>
                  <p className="text-xs text-gray-400">{card.subLabel}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Recent Activity Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FiActivity className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-800">Recent Activity</h3>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentActivities.map((activity, index) => (
              <div key={activity.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{activity.action}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-gray-500">{activity.user}</span>
                    {activity.amount && (
                      <span className="text-xs font-semibold text-emerald-600">{activity.amount}</span>
                    )}
                    <span className="text-xs text-gray-400">{activity.time}</span>
                  </div>
                </div>
                <FiEye className="w-4 h-4 text-gray-400 hover:text-indigo-600 cursor-pointer transition" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Footer Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Last updated: {new Date().toLocaleString()} • All data is real-time
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;